import React, { useState, useEffect, useContext, useCallback } from 'react';
import classNames from 'classnames/bind';
import styles from './VoucherManagement.module.css';
import { ToastContext } from '~/components/context/ToastContext';
import { ModalContext } from '~/components/context/ModalContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faSave, faSyncAlt, faEdit } from '@fortawesome/free-solid-svg-icons';
import { BASE_VOUCHER_URL } from '~/helper/apiService';

const cx = classNames.bind(styles);

function VoucherManagement() {
  const [vouchers, setVouchers] = useState([]);
  const [newVoucherName, setNewVoucherName] = useState('');
  const [newVoucherQuantity, setNewVoucherQuantity] = useState(0);
  const [newVoucherExpiry, setNewVoucherExpiry] = useState('');
  const [editingVoucher, setEditingVoucher] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vouchersLoading, setVouchersLoading] = useState(true);
  const { showToast } = useContext(ToastContext);
  const { showModal } = useContext(ModalContext);

  // Fetch vouchers
  const fetchVouchers = useCallback(async () => {
    try {
      setVouchersLoading(true);
      const response = await fetch(`${BASE_VOUCHER_URL}vouchers`, {
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        setVouchers(data || []);
      } else {
        showToast(data.error || 'Lỗi khi lấy thông tin voucher', 'error');
      }
    } catch (err) {
      showToast('Lỗi kết nối đến server: ' + err.message, 'error');
    } finally {
      setVouchersLoading(false);
    }
  }, [showToast]);

  // Fetch vouchers on mount
  useEffect(() => {
    fetchVouchers();
  }, [fetchVouchers]);

  // Handle adding new voucher
  const handleAddVoucher = useCallback(async () => {
    if (!newVoucherName || !newVoucherExpiry) {
      showToast('Vui lòng nhập đầy đủ: tên, ngày hết hạn', 'error');
      return;
    }
    if (!Number.isInteger(Number(newVoucherQuantity)) || Number(newVoucherQuantity) < 0) {
      showToast('Số lượng phải là số nguyên >= 0', 'error');
      return;
    }
    const expiryDate = new Date(newVoucherExpiry);
    if (isNaN(expiryDate.getTime())) {
      showToast('Ngày hết hạn không hợp lệ', 'error');
      return;
    }

    const tempId = Date.now();
    const newVoucher = {
      id: tempId,
      name: newVoucherName,
      quantity: Number(newVoucherQuantity),
      expiry: newVoucherExpiry,
    };
    setVouchers((prev) => [...prev, newVoucher]);

    try {
      setLoading(true);
      const response = await fetch(`${BASE_VOUCHER_URL}vouchers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newVoucherName,
          quantity: Number(newVoucherQuantity),
          expiry: newVoucherExpiry,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        showToast('Tạo voucher thành công', 'success');
        fetchVouchers();
        setNewVoucherName('');
        setNewVoucherQuantity(0);
        setNewVoucherExpiry('');
      } else {
        showToast(data.error || 'Lỗi khi tạo voucher', 'error');
        setVouchers((prev) => prev.filter((voucher) => voucher.id !== tempId));
      }
    } catch (error) {
      showToast('Lỗi kết nối: ' + error.message, 'error');
      setVouchers((prev) => prev.filter((voucher) => voucher.id !== tempId));
    } finally {
      setLoading(false);
    }
  }, [newVoucherName, newVoucherQuantity, newVoucherExpiry, showToast, fetchVouchers]);

  // Handle editing voucher
  const handleEditVoucher = useCallback(async (voucher) => {
    if (!voucher.name || !voucher.expiry) {
      showToast('Vui lòng nhập đầy đủ: tên, ngày hết hạn', 'error');
      return;
    }
    if (!Number.isInteger(Number(voucher.quantity)) || Number(voucher.quantity) < 0) {
      showToast('Số lượng phải là số nguyên >= 0', 'error');
      return;
    }
    const expiryDate = new Date(voucher.expiry);
    if (isNaN(expiryDate.getTime())) {
      showToast('Ngày hết hạn không hợp lệ', 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${BASE_VOUCHER_URL}vouchers/${voucher.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: voucher.name,
          quantity: Number(voucher.quantity),
          expiry: voucher.expiry,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        showToast('Cập nhật voucher thành công', 'success');
        fetchVouchers();
        setEditingVoucher(null);
      } else {
        showToast(data.error || 'Lỗi khi cập nhật voucher', 'error');
      }
    } catch (error) {
      showToast('Lỗi kết nối: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, fetchVouchers]);

  // Handle deleting voucher
  const handleDeleteVoucher = useCallback(async (id) => {
    const deletedVoucher = vouchers.find((voucher) => voucher.id === id);
    setVouchers((prev) => prev.filter((voucher) => voucher.id !== id));

    try {
      setLoading(true);
      const response = await fetch(`${BASE_VOUCHER_URL}vouchers/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        showToast('Xóa voucher thành công', 'success');
        fetchVouchers();
      } else {
        showToast(data.error || 'Lỗi khi xóa voucher', 'error');
        setVouchers((prev) => [...prev, deletedVoucher]);
      }
    } catch (error) {
      showToast('Lỗi kết nối: ' + error.message, 'error');
      setVouchers((prev) => [...prev, deletedVoucher]);
    } finally {
      setLoading(false);
    }
  }, [vouchers, showToast, fetchVouchers]);

  // Confirm delete voucher
  const confirmDeleteVoucher = useCallback(
    (voucher) => {
      showModal({
        title: 'Xác Nhận Xóa',
        message: `Bạn có chắc chắn muốn xóa voucher ${voucher.name}?`,
        onConfirm: () => handleDeleteVoucher(voucher.id),
        onCancel: null,
      });
    },
    [showModal, handleDeleteVoucher]
  );

  // Handle reloading voucher data
  const handleReloadVouchers = useCallback(async () => {
    await fetchVouchers();
    showToast('Tải lại dữ liệu voucher thành công', 'success');
  }, [fetchVouchers, showToast]);

  const isExpired = (expiry) => {
    const expiryDate = new Date(expiry);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiryDate.setHours(0, 0, 0, 0);
    return expiryDate < today;
  };

  const isOutOfStock = (quantity) => quantity <= 0;

  const getStatusBadge = (voucher) => {
    const expired = isExpired(voucher.expiry);
    const outOfStock = isOutOfStock(voucher.quantity);
    if (expired && outOfStock) {
      return 'Hết hạn & Hết số lượng';
    } else if (expired) {
      return 'Hết hạn';
    } else if (outOfStock) {
      return 'Hết số lượng';
    }
    return null;
  };

  const isInactive = (voucher) => isExpired(voucher.expiry) || isOutOfStock(voucher.quantity);

  return (
    <div className={cx('content')}>
      <div className={cx('voucher-section')}>
        <div className={cx('voucher-form')}>
          <h3 className={cx('section-title')}>
            {editingVoucher ? 'Chỉnh Sửa Voucher' : 'Thêm Voucher Mới'}
          </h3>
          <div className={cx('form-row')}>
            <label className={cx('form-label')}>Tên Voucher:</label>
            <input
              type="text"
              value={editingVoucher ? editingVoucher.name : newVoucherName}
              onChange={(e) =>
                editingVoucher
                  ? setEditingVoucher({ ...editingVoucher, name: e.target.value })
                  : setNewVoucherName(e.target.value)
              }
              className={cx('input')}
              placeholder="Nhập tên voucher"
            />
          </div>
          <div className={cx('form-row')}>
            <label className={cx('form-label')}>Số Lượng:</label>
            <input
              type="number"
              value={editingVoucher ? editingVoucher.quantity : newVoucherQuantity}
              onChange={(e) =>
                editingVoucher
                  ? setEditingVoucher({ ...editingVoucher, quantity: e.target.value })
                  : setNewVoucherQuantity(e.target.value)
              }
              className={cx('input')}
              min="0"
              placeholder="Nhập số lượng"
            />
          </div>
          <div className={cx('form-row')}>
            <label className={cx('form-label')}>Ngày Hết Hạn:</label>
            <input
              type="date"
              value={editingVoucher ? editingVoucher.expiry : newVoucherExpiry}
              onChange={(e) =>
                editingVoucher
                  ? setEditingVoucher({ ...editingVoucher, expiry: e.target.value })
                  : setNewVoucherExpiry(e.target.value)
              }
              className={cx('input')}
            />
          </div>
          <div className={cx('form-actions')}>
            <button
              onClick={() => editingVoucher ? handleEditVoucher(editingVoucher) : handleAddVoucher()}
              disabled={loading}
              className={cx('button', 'save-button')}
            >
              <FontAwesomeIcon icon={faSave} className={cx('button-icon')} />
              <span className={cx('button-text')}>
                {editingVoucher ? 'Cập Nhật' : 'Thêm Mới'}
              </span>
            </button>
            {editingVoucher && (
              <button
                onClick={() => setEditingVoucher(null)}
                disabled={loading}
                className={cx('button', 'stop-button')}
              >
                <FontAwesomeIcon icon={faTrash} className={cx('button-icon')} />
                <span className={cx('button-text')}>Hủy</span>
              </button>
            )}
          </div>
        </div>
        <div className={cx('vouchers-list')}>
          <div className={cx('list-header')}>
            <h3 className={cx('section-title')}>Danh Sách Voucher</h3>
            <div className={cx('list-actions')}>
              <button
                onClick={handleReloadVouchers}
                disabled={loading}
                className={cx('button', 'reload-button')}
              >
                <FontAwesomeIcon icon={faSyncAlt} className={cx('button-icon')} />
                <span className={cx('button-text')}>Tải lại</span>
              </button>
            </div>
          </div>
          {vouchersLoading && vouchers.length === 0 ? (
            <p className={cx('loading')}>Đang tải...</p>
          ) : vouchers.length === 0 ? (
            <p className={cx('no-data')}>Không có voucher nào</p>
          ) : (
            vouchers.map((voucher, index) => {
              const statusBadge = getStatusBadge(voucher);
              const inactive = isInactive(voucher);
              return (
                <div key={voucher.id} className={cx('voucher-card', { inactive })}>
                  <div className={cx('card-info')}>
                    <span className={cx('card-label')}>STT: {index + 1}</span>
                    <span className={cx('card-value')}>ID: {voucher.id}</span>
                    <span className={cx('card-value')}>Tên: {voucher.name}</span>
                    <span className={cx('card-value', { inactive: isOutOfStock(voucher.quantity) })}>Số Lượng: {voucher.quantity}</span>
                    <span className={cx('card-value', { inactive: isExpired(voucher.expiry) })}>
                      HSD: {new Date(voucher.expiry).toLocaleDateString('vi-VN')}
                    </span>
                    {statusBadge && (
                      <span className={cx('status-badge', { inactive })}>{statusBadge}</span>
                    )}
                  </div>
                  <div className={cx('card-actions')}>
                    <button
                      onClick={() => setEditingVoucher(voucher)}
                      disabled={loading}
                      className={cx('button', 'edit-button')}
                    >
                      <FontAwesomeIcon icon={faEdit} className={cx('button-icon')} />
                      <span className={cx('button-text')}>Sửa</span>
                    </button>
                    <button
                      onClick={() => confirmDeleteVoucher(voucher)}
                      disabled={loading}
                      className={cx('button', 'stop-button')}
                    >
                      <FontAwesomeIcon icon={faTrash} className={cx('button-icon')} />
                      <span className={cx('button-text')}>Xóa</span>
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default VoucherManagement;